import { getDB, deleteDB, STORES } from './db'
import { generateDemoData } from './demoData'

/**
 * Single data-access layer for the whole app (ТЗ рекомендация №6).
 *
 * Today it reads/writes IndexedDB (+ localStorage for the seed flag). Tomorrow
 * these same method signatures can be backed by an HTTP client (PHP Slim API)
 * without touching any component. Nothing outside this module should import
 * `db.js` directly.
 */

const SEED_FLAG = 'bvxtb-demo-seeded'

// ---- seeding / lifecycle -------------------------------------------------

export const isSeeded = () => localStorage.getItem(SEED_FLAG) === '1'

const bulkPut = async (storeName, rows) => {
	if (!rows || !rows.length) return
	const db = await getDB()
	const tx = db.transaction(storeName, 'readwrite')
	await Promise.all(rows.map(row => tx.store.put(row)))
	await tx.done
}

export const seedDemoData = async () => {
	const data = generateDemoData()
	await bulkPut(STORES.schoolsStats, data.schoolsStats)
	await bulkPut(STORES.teachersStats, data.teachersStats)
	await bulkPut(STORES.uploadHistory, data.uploadHistory)
	await bulkPut(STORES.departmentDocuments, data.departmentDocuments)
	await bulkPut(STORES.files, data.files)
	localStorage.setItem(SEED_FLAG, '1')
}

// Called once on app start; seeds only if empty. Guarded by a singleton
// promise so React StrictMode's double-invoked effect (dev) can't seed twice.
let initPromise = null

const runInit = async () => {
	await getDB()
	if (!isSeeded()) {
		await seedDemoData()
	}
}

export const initData = () => {
	if (!initPromise) {
		initPromise = runInit()
	}
	return initPromise
}

export const clearAllData = async () => {
	await deleteDB()
	localStorage.removeItem(SEED_FLAG)
}

export const regenerateDemoData = async () => {
	await clearAllData()
	await getDB()
	await seedDemoData()
}

// ---- statistics datasets -------------------------------------------------

export const getSchoolsStats = async () => (await getDB()).getAll(STORES.schoolsStats)
export const getTeachersStats = async () => (await getDB()).getAll(STORES.teachersStats)

// id = `${districtId}__${period}` → put() upserts (Вопрос 4: перезапись).
export const upsertSchoolsRow = async (row) => (await getDB()).put(STORES.schoolsStats, row)
export const upsertTeachersRow = async (row) => (await getDB()).put(STORES.teachersStats, row)

export const getSchoolsRow = async (districtId, period) =>
	(await getDB()).get(STORES.schoolsStats, `${districtId}__${period}`)
export const getTeachersRow = async (districtId, period) =>
	(await getDB()).get(STORES.teachersStats, `${districtId}__${period}`)

// ---- upload history (statistics module) ----------------------------------

export const getUploadHistory = async (moduleId) => {
	const db = await getDB()
	const all = moduleId
		? await db.getAllFromIndex(STORES.uploadHistory, 'moduleId', moduleId)
		: await db.getAll(STORES.uploadHistory)
	return all.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
}

export const addUploadHistory = async (entry) =>
	(await getDB()).put(STORES.uploadHistory, entry)

export const deleteUploadHistory = async (id) => {
	const db = await getDB()
	const entry = await db.get(STORES.uploadHistory, id)
	await db.delete(STORES.uploadHistory, id)
	if (entry?.fileId) await db.delete(STORES.files, entry.fileId)
}

// Upsert a statistics row into the given store ('schoolsStats' | 'teachersStats').
export const upsertStatRow = async (store, row) => (await getDB()).put(store, row)

// ---- department documents (depts 1–4) ------------------------------------

export const getDepartmentDocuments = async (subsectionId) => {
	const db = await getDB()
	const all = subsectionId
		? await db.getAllFromIndex(STORES.departmentDocuments, 'subsectionId', subsectionId)
		: await db.getAll(STORES.departmentDocuments)
	return all.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
}

export const addDepartmentDocument = async (doc) =>
	(await getDB()).put(STORES.departmentDocuments, doc)

export const deleteDepartmentDocument = async (id) => {
	const db = await getDB()
	const doc = await db.get(STORES.departmentDocuments, id)
	await db.delete(STORES.departmentDocuments, id)
	if (doc?.fileId) await db.delete(STORES.files, doc.fileId)
}

// ---- raw files -----------------------------------------------------------

export const saveFile = async (file) => (await getDB()).put(STORES.files, file)
export const getFile = async (id) => (await getDB()).get(STORES.files, id)

const dataService = {
	isSeeded,
	initData,
	seedDemoData,
	clearAllData,
	regenerateDemoData,
	getSchoolsStats,
	getTeachersStats,
	upsertSchoolsRow,
	upsertTeachersRow,
	getSchoolsRow,
	getTeachersRow,
	getUploadHistory,
	addUploadHistory,
	deleteUploadHistory,
	upsertStatRow,
	getDepartmentDocuments,
	addDepartmentDocument,
	deleteDepartmentDocument,
	saveFile,
	getFile,
}

export default dataService
